import React from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Box,
  VStack,
  HStack,
  Text,
  Button,
  useToast,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { List, ListPlus, Lock } from 'lucide-react';
import { useCreateList } from '@/api';
import { useNavigate } from '@tanstack/react-router';
import { ListFormFields, useListFormState } from '@/components/forms';
import { Haptics, ImpactStyle, NotificationType } from '@capacitor/haptics';
import { useFeatureAccess } from '@/hooks/useFeatureAccess';
import { useGetLists } from '@/api/queries/useListsQueries';
import { LockedFeature } from '@/components/subscription/LockedFeature';
import { UpgradeCtaButton } from '@/components/subscription/UpgradeCtaButton';

const MotionBox = motion.create(Box);

interface CreateListModalProps {
  isOpen: boolean;
  onClose: () => void;
  onListCreated?: () => void;
}

/**
 * CreateListModal - Modal for creating a new personal restaurant list
 */
export function CreateListModal({
  isOpen,
  onClose,
  onListCreated,
}: CreateListModalProps) {
  const toast = useToast();
  const navigate = useNavigate();

  const {
    values,
    nameError,
    handleNameChange,
    setDescription,
    setCoverArt,
    validateName,
    reset,
    isValid,
  } = useListFormState();

  const { mutateAsync: createList, isPending: isCreating } = useCreateList();

  // Get current list count for limit check
  const { data: lists } = useGetLists();
  const currentListCount = lists?.ownedLists?.length ?? 0;

  // Check subscription limits for personal lists
  const { hasAccess, limitReached, remaining } = useFeatureAccess('personal_lists', currentListCount);
  const isAtLimit = limitReached && !hasAccess;

  const handleCreate = async () => {
    if (!validateName(values.name)) return;

    try {
      await Haptics.impact({ style: ImpactStyle.Heavy });
    } catch {
      // Haptics not available
    }

    try {
      const result = await createList({
        listName: values.name.trim(),
        coverArt: values.coverArt || undefined,
      });

      try {
        await Haptics.notification({ type: NotificationType.Success });
      } catch {
        // Haptics not available
      }

      toast({
        title: 'List created!',
        description: 'Start adding your favorite restaurants.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      reset();
      onClose();

      if (onListCreated) {
        onListCreated();
      }

      // Navigate to the new list
      if (result.urlKey) {
        navigate({ to: '/lists/$urlKey', params: { urlKey: result.urlKey } });
      }
    } catch (error) {
      toast({
        title: 'Failed to create list',
        description: 'Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md" isCentered>
      <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(8px)" />
      <ModalContent bg="var(--card-bg)" borderRadius="var(--r-card)" mx={4}>
        <ModalHeader>
          <HStack spacing={3}>
            <Box
              bg={isAtLimit ? 'orange.100' : 'purple.100'}
              borderRadius="var(--r-inner)"
              p={2}
            >
              {isAtLimit ? (
                <Lock size={20} color="var(--chakra-colors-orange-500)" />
              ) : (
                <List size={20} color="var(--chakra-colors-purple-500)" />
              )}
            </Box>
            <VStack align="start" spacing={0}>
              <Text fontSize="lg" fontWeight="bold">
                {isAtLimit ? 'List Limit Reached' : 'Create New List'}
              </Text>
              <Text fontSize="xs" color="var(--card-text-3)" fontWeight="normal">
                {isAtLimit
                  ? `You've used all ${currentListCount} of your personal lists`
                  : 'Organize your favorite restaurants'}
              </Text>
            </VStack>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody py={4}>
          {isAtLimit ? (
            // Upgrade prompt when at limit
            <VStack spacing={4} align="stretch" py={4}>
              {/* The footer owns the CTA (beside "Maybe Later"), so the banner has none. */}
              <LockedFeature
                variant="banner"
                align="center"
                showCta={false}
                locked
                context="unlimitedLists"
                headline="Upgrade to Paire+"
                body="Create unlimited personal lists and organize all your favorite dining spots without restrictions."
              />
              <Text fontSize="xs" color="var(--card-text-3)" textAlign="center">
                Free accounts are limited to 3 personal lists
              </Text>
            </VStack>
          ) : (
            // Normal form
            <VStack spacing={5} align="stretch">
              {/* Remaining count indicator */}
              {remaining !== null && remaining !== undefined && remaining <= 2 && (
                <Box
                  bg="orange.50"
                  borderRadius="var(--r-inner)"
                  px={3}
                  py={2}
                  borderWidth="1px"
                  borderColor="orange.200"
                >
                  <Text fontSize="xs" color="orange.600" fontWeight="medium">
                    {remaining === 0
                      ? "This will be your last free list"
                      : `${remaining} list${remaining === 1 ? '' : 's'} remaining on free plan`}
                  </Text>
                </Box>
              )}
              {/* Shared form fields */}
              <ListFormFields
                values={values}
                onNameChange={handleNameChange}
                onDescriptionChange={setDescription}
                onCoverArtChange={setCoverArt}
                nameError={nameError}
                onNameBlur={() => validateName(values.name)}
                namePlaceholder="e.g., Date Night Spots"
                colorScheme="purple"
                showDescription={false}
              />
            </VStack>
          )}
        </ModalBody>

        <ModalFooter borderTop="1px solid" borderColor="var(--card-divider)" pt={4}>
          <HStack spacing={3} w="100%">
            <Button
              variant="ghost"
              onClick={handleClose}
              flex={1}
              minH="44px"
            >
              {isAtLimit ? 'Maybe Later' : 'Cancel'}
            </Button>
            {isAtLimit ? (
              // Closes this modal FIRST (`onClose`, not the form-resetting `handleClose`),
              // then opens the upgrade prompt on top.
              <UpgradeCtaButton
                context="unlimitedLists"
                onBeforeUpgrade={onClose}
                layout={{ flex: 1 }}
              />
            ) : (
              <Button
                bg="purple.500"
                color="white"
                _hover={{ bg: 'purple.600' }}
                onClick={handleCreate}
                isLoading={isCreating}
                isDisabled={!isValid}
                leftIcon={<ListPlus size={16} />}
                flex={1}
                minH="44px"
              >
                Create List
              </Button>
            )}
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default CreateListModal;
